import * as React from "react"

import { cn } from "@/lib/utils"
import { ChevronDownIcon } from "lucide-react"

type NativeSelectProps = Omit<React.ComponentProps<"select">, "size"> & {
  size?: "sm" | "default"
  /**
   * Dense tier (Document Designer inspector, G4): 28px tall, 12px text, 8px
   * left padding. Independent of `size` — when both are given, dense wins.
   * Default is unchanged.
   */
  density?: "default" | "dense"
}

const NativeSelect = React.forwardRef<HTMLSelectElement, NativeSelectProps>(
  ({ className, size = "default", density = "default", ...props }, ref) => {
    const dense = density === "dense"
    return (
      <div
        className={cn(
          "group/native-select relative w-fit has-[select:disabled]:opacity-50",
          className
        )}
        data-slot="native-select-wrapper"
        data-size={size}
        data-density={density}
      >
        <select
          ref={ref}
          data-slot="native-select"
          data-size={size}
          data-density={density}
          // IC-555: bg-background, not bg-transparent — see ui/input.tsx's comment.
          // Dense overrides come last so tailwind-merge resolves them against the
          // base `h-9` / `pl-2.5` / `pr-8` / `text-sm` / `data-[size=sm]:h-8`.
          className={cn(
            "h-9 w-full min-w-0 appearance-none rounded-md border border-input bg-background py-1 pr-8 pl-2.5 text-sm shadow-xs transition-[color,box-shadow] outline-none select-none selection:bg-primary selection:text-primary-foreground placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 data-[size=sm]:h-8 dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40",
            dense && "h-7 py-0 pr-7 pl-2 text-xs data-[size=sm]:h-7"
          )}
          {...props}
        />
        <ChevronDownIcon
          className={cn(
            "pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-muted-foreground select-none",
            dense && "right-2 size-3.5"
          )}
          aria-hidden="true"
          data-slot="native-select-icon"
        />
      </div>
    )
  }
)
NativeSelect.displayName = "NativeSelect"

function NativeSelectOption({
  className,
  ...props
}: React.ComponentProps<"option">) {
  return (
    <option
      data-slot="native-select-option"
      className={cn("bg-[Canvas] text-[CanvasText]", className)}
      {...props}
    />
  )
}

function NativeSelectOptGroup({
  className,
  ...props
}: React.ComponentProps<"optgroup">) {
  return (
    <optgroup
      data-slot="native-select-optgroup"
      className={cn("bg-[Canvas] text-[CanvasText]", className)}
      {...props}
    />
  )
}

export { NativeSelect, NativeSelectOptGroup, NativeSelectOption }
